import { useState, useCallback, useRef } from 'react'
import {
  DemoRegistrationRequest,
  DemoRegistrationResponse,
  DemoSession,
} from '../types'
import { demoSessionStorage } from '../lib/demoSession'

const getCsrfToken = (): string | null => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') ?? null
}

/** 連続リクエストを防ぐための最小間隔（ミリ秒） */
const RATE_LIMIT_INTERVAL_MS = 5000

type UseDemoRegistrationResult = {
  register: (request: DemoRegistrationRequest) => Promise<DemoSession>
  isLoading: boolean
  error: string | null
}

export const useDemoRegistration = (): UseDemoRegistrationResult => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastRequestTimeRef = useRef<number>(0)

  const register = useCallback(
    async (request: DemoRegistrationRequest): Promise<DemoSession> => {
      // レート制限対策
      const now = Date.now()
      if (now - lastRequestTimeRef.current < RATE_LIMIT_INTERVAL_MS) {
        throw new Error('しばらくお待ちください')
      }
      lastRequestTimeRef.current = now

      setIsLoading(true)
      setError(null)

      try {
        const csrfToken = getCsrfToken()
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken
        }

        const response = await fetch('/exams/demo/registration', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ...request,
            trafficSource: window.location.href,
          }),
        })

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('リクエストが多すぎます。しばらくお待ちください')
          }
          const errorData = (await response.json()) as { err?: string }
          throw new Error(errorData.err || '登録に失敗しました')
        }

        const result = (await response.json()) as DemoRegistrationResponse

        const session: DemoSession = {
          examineeKey: result.examineeKey,
          token: result.token,
          scheduleKey: result.scheduleKey,
          scheduleName: result.scheduleInfo.name,
          durationMins: result.scheduleInfo.durationMins,
          registeredAt: new Date().toISOString(),
        }

        demoSessionStorage.save(session)
        return session
      } catch (e) {
        const message =
          e instanceof Error ? e.message : '不明なエラーが発生しました'
        setError(message)
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  return { register, isLoading, error }
}
